import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHistory, FaRocket, FaCogs, FaUsers } from 'react-icons/fa';
import { ink, paper, paperMuted, borderInk, bodyMuted, sky } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const AboutSection = styled.section`
  padding: 80px 0;
  background: ${props => (props.$muted ? paperMuted : paper)};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HistoryBlock = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(3, 26, 56, 0.04),
    0 8px 24px rgba(3, 26, 56, 0.04);

  @media (max-width: 768px) {
    padding: 30px 25px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
    margin-bottom: 25px;
  }
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: -40px -40px 24px -40px;
  padding: 16px 40px 18px calc(40px + 5px);
  position: relative;
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
    filter: blur(0.2px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    margin: -30px -25px 22px -25px;
    padding: 14px 25px 16px calc(25px + 5px);
  }

  @media (max-width: 480px) {
    margin: -25px -20px 20px -20px;
    padding: 12px 20px 14px calc(20px + 5px);
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const BlockIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(142, 207, 255, 0.45);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${sky};
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const BlockTitle = styled.h3`
  position: relative;
  z-index: 1;
  font-size: 1.65rem;
  color: #f2f7ff;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin: 0;
  flex: 1;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -6px;
    width: 3rem;
    height: 2px;
    background: linear-gradient(90deg, ${sky} 0%, transparent 100%);
    border-radius: 1px;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const BlockPeriod = styled.div`
  position: relative;
  z-index: 1;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-left: auto;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);

  @media (max-width: 480px) {
    margin-left: 0;
    align-self: flex-end;
    font-size: 0.8rem;
  }
`;

const BlockDescription = styled.p`
  color: ${bodyMuted};
  line-height: 1.75;
  font-size: 1.05rem;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.65;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.55;
  }
`;

const BlockList = styled.ul`
  color: ${bodyMuted};
  line-height: 1.75;
  font-size: 0.98rem;
  margin-left: 20px;
  margin-bottom: 0;

  li {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: ${ink};
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 0.92rem;
    line-height: 1.65;
  }

  @media (max-width: 480px) {
    font-size: 0.88rem;
    line-height: 1.55;
    margin-left: 15px;
  }
`;

const PrinciplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const PrincipleCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 30px;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const PrincipleNumber = styled.div`
  font-size: 2rem;
  color: ${ink};
  font-weight: 800;
  margin-bottom: 15px;
  letter-spacing: 0.04em;

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 12px;
  }
`;

const PrincipleText = styled.p`
  color: ${bodyMuted};
  line-height: 1.65;
  font-size: 1.05rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.55;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const TeamBlock = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  margin-top: 3rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);

  @media (max-width: 768px) {
    padding: 30px 25px;
    margin-top: 2.5rem;
  }

  @media (max-width: 480px) {
    padding: 25px 20px;
    margin-top: 2rem;
  }
`;

const TeamHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: -40px -40px 22px -40px;
  padding: 16px 40px 18px calc(40px + 5px);
  position: relative;
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
    filter: blur(0.2px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    margin: -30px -25px 20px -25px;
    padding: 14px 25px 16px calc(25px + 5px);
  }

  @media (max-width: 480px) {
    margin: -25px -20px 18px -20px;
    padding: 12px 20px 14px calc(20px + 5px);
  }
`;

const TeamIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(142, 207, 255, 0.45);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${sky};
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const TeamTitle = styled.h3`
  position: relative;
  z-index: 1;
  font-size: 1.05rem;
  color: #f2f7ff;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.88rem;
  }
`;

const TeamText = styled.p`
  color: ${bodyMuted};
  line-height: 1.75;
  font-size: 1.05rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.65;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.55;
  }
`;

const AboutCompany = () => {
  return (
    <>
      <AboutSection id="about">
        <Container>
          <BlueprintSectionTitle>История развития</BlueprintSectionTitle>

          <HistoryBlock
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <BlockHeader>
              <BlockIcon>
                <FaHistory />
              </BlockIcon>
              <BlockTitle>Истоки</BlockTitle>
              <BlockPeriod>2005–2015</BlockPeriod>
            </BlockHeader>
            <BlockDescription>
              Автоматизация «в поле»
            </BlockDescription>
            <BlockDescription>
              С самого начала деятельности перед компанией были поставлены важные задачи обеспечения безопасности и бесперебойности объектов. Команда стартовала с внедрения систем телемеханики и сбора данных на действующих объектах: электростанциях, подстанциях, узлах учета.
            </BlockDescription>
            <BlockDescription>
              Ключевые задачи того периода:
            </BlockDescription>
            <BlockList>
              <li><strong>Объединение данных:</strong> Создание единого контура управленческого контроля, связывающего разрозненные энергообъекты.</li>
              <li><strong>Отчетность для людей:</strong> Разработка интерфейсов и отчетных форм, удобных для эксплуатационного персонала любого уровня — от сменного инженера до руководителя департамента.</li>
              <li><strong>Референсные проекты:</strong> Успешный запуск и сопровождение объектов энергетики, РЖД, генерирующих компаний.</li>
            </BlockList>
          </HistoryBlock>

          <HistoryBlock
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <BlockHeader>
              <BlockIcon>
                <FaRocket />
              </BlockIcon>
              <BlockTitle>Эволюция</BlockTitle>
              <BlockPeriod>2015–2022</BlockPeriod>
            </BlockHeader>
            <BlockDescription>
              Бизнес-процессы и аналитика
            </BlockDescription>
            <BlockDescription>
              Накопленная экспертиза позволила масштабировать решения за пределы энергетики. Автоматизация вышла на уровень управления технологическими бизнес-процессами:
            </BlockDescription>
            <BlockList>
              <li><strong>Логистика и учет:</strong> Автоматизация погрузки угля, контроль уровней в резервуарах, диспетчеризация транспорта.</li>
              <li><strong>Управление оборудованием:</strong> Дистанционный пуск/останов, предиктивная диагностика, предотвращение аварийных остановок.</li>
              <li><strong>Качество данных:</strong> Решение задач анализа работы систем АСДУ / СДТУ / АСУ ТП. Валидация телеметрии, оценка задержек, аудит каналов связи.</li>
            </BlockList>
            <BlockDescription style={{ marginTop: '20px' }}>
              Параллельно развивалась компетенция в области безопасности:
            </BlockDescription>
            <BlockList>
              <li>Разработка модулей для сетевых устройств и межсетевых экранов.</li>
              <li>Создание ПО для контроллеров АСУ ТП: от драйверов до логики аварийной защиты.</li>
            </BlockList>
          </HistoryBlock>

          <HistoryBlock
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <BlockHeader>
              <BlockIcon>
                <FaCogs />
              </BlockIcon>
              <BlockTitle>Настоящее</BlockTitle>
            </BlockHeader>
            <BlockDescription>
              Модульная платформа E-node
            </BlockDescription>
            <BlockDescription>
              Сегодня «Энткор-е» реализует накопленный опыт проектной деятельности в линейку продуктов собственной разработки. Реализуемый подход выглядит так: проверенные с точки зрения надежности в критической инфраструктуре решения должны тиражироваться в виде универсальных решений для всех отраслей и при необходимости дорабатываться с учетом их специфики силами партнеров.
            </BlockDescription>
            <BlockDescription>
              Что это значит для заказчика:
            </BlockDescription>
            <BlockList>
              <li><strong>Надежность «по умолчанию»:</strong> Архитектура, устойчивая к сбоям, потому что создавалась для объектов, где нельзя просто «перезагрузить».</li>
              <li><strong>Глубокая интеграция:</strong> Поддержка промышленных протоколов (OPC UA, Modbus, IEC 61850) «из коробки».</li>
              <li><strong>Понимание контекста:</strong> Продукты говорят на языке инженеров, а не маркетологов.</li>
              <li><strong>В основе лежит бизнес-процесс:</strong> подходя к задаче наши специалисты детально изучают не только проблему Заказчика, но и бизнес-процессы на которую она влияет. В результате технические решения дополняют бизнес-процессы потребителя, с одной стороны повышая их надежность, а с другой – снижая нагрузку на обеспечивающий процессы персонал.</li>
            </BlockList>
          </HistoryBlock>
        </Container>
      </AboutSection>

      <AboutSection $muted>
        <Container>
          <BlueprintSectionTitle>Основные принципы</BlueprintSectionTitle>
          <BlockDescription style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
            В основе подхода — три принципа, продиктованные практикой:
          </BlockDescription>
          <PrinciplesGrid>
            <PrincipleCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <PrincipleNumber>1</PrincipleNumber>
              <PrincipleText>
                Данные должны работать. Не просто собираться, а становиться основой для решений в реальном времени.
              </PrincipleText>
            </PrincipleCard>

            <PrincipleCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <PrincipleNumber>2</PrincipleNumber>
              <PrincipleText>
                Сложность — внутри, простота — снаружи. Пользователь видит интуитивный интерфейс, а не архитектуру микросервисов.
              </PrincipleText>
            </PrincipleCard>

            <PrincipleCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <PrincipleNumber>3</PrincipleNumber>
              <PrincipleText>
                Безопасность по умолчанию. Защита закладывается на этапе проектирования, а не добавляется постфактум.
              </PrincipleText>
            </PrincipleCard>
          </PrinciplesGrid>
        </Container>
      </AboutSection>

      <AboutSection>
        <Container>
          <BlueprintSectionTitle>Команда</BlueprintSectionTitle>
          <TeamBlock
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <TeamHeader>
              <TeamIcon>
                <FaUsers />
              </TeamIcon>
              <TeamTitle>Команда</TeamTitle>
            </TeamHeader>
            <TeamText>
              Продукты развивают инженеры и разработчики с бэкграундом в системной интеграции, АСУ и информационной безопасности, которые прошли путь от внедрения систем на объектах, что позволяет понимать специфику бизнес-процессов, разработки универсальных ИТ решений, которые вобрали в себя весь предыдущий опыт.
            </TeamText>
          </TeamBlock>
        </Container>
      </AboutSection>
    </>
  );
};

export default AboutCompany;
